import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography } from 'antd';
import {
  UnorderedListOutlined,
  MessageOutlined,
  PictureOutlined,
  KeyOutlined,
  RobotOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { logout } from '../auth';

const { Sider } = Layout;
const { Title } = Typography;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      key: 'datasets-group',
      label: 'Datasets',
      type: 'group' as const,
      children: [
        {
          key: '/datasets',
          icon: <UnorderedListOutlined />,
          label: 'Datasets',
        },
      ],
    },
    {
      key: 'prompts-group',
      label: 'Prompts',
      type: 'group' as const,
      children: [
        {
          key: '/prompts',
          icon: <MessageOutlined />,
          label: 'Prompts',
        },
      ],
    },
    {
      key: 'tasks-group',
      label: 'Tasks',
      type: 'group' as const,
      children: [
        {
          key: '/captioning',
          icon: <RobotOutlined />,
          label: 'Captioning Tasks',
        },
      ],
    },
    {
      key: 'moderation-group',
      label: 'Moderation',
      type: 'group' as const,
      children: [
        {
          key: '/moderation',
          icon: <EyeOutlined />,
          label: 'Moderation',
        },
        {
          key: '/auto-moderation',
          icon: <ThunderboltOutlined />,
          label: 'Auto-Moderation',
        },
      ],
    },
    {
      key: 'settings-group',
      label: 'Settings',
      type: 'group' as const,
      children: [
        {
          key: '/api-keys',
          icon: <KeyOutlined />,
          label: 'API Keys',
        },
      ],
    },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '/datasets') return '/datasets';
    if (path === '/datasets/create') return '/datasets';
    if (path.startsWith('/datasets/')) return '/datasets';
    if (path.startsWith('/prompts')) return '/prompts';
    if (path === '/api-keys') return '/api-keys';
    if (path === '/captioning') return '/captioning';
    if (path === '/captioning/create') return '/captioning';
    if (path.startsWith('/captioning/')) return '/captioning';
    if (path === '/moderation') return '/moderation';
    if (path === '/moderation/create') return '/moderation';
    if (path.startsWith('/moderation/')) return '/moderation';
    if (path.startsWith('/auto-moderation')) return '/auto-moderation';
    return '/datasets';
  };

  return (
    <Sider
      width={260}
      collapsedWidth={80}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        minHeight: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
    >
      <div style={{
        padding: collapsed ? '20px 16px' : '20px 24px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: '12px'
      }}>
        {!collapsed && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <PictureOutlined style={{ fontSize: '24px', color: '#6366f1' }} />
              <Title level={4} style={{ margin: 0, color: 'white' }}>
                Image Manager
              </Title>
            </div>
          </>
        )}
        <div
          onClick={() => onCollapse(!collapsed)}
          style={{ cursor: 'pointer', color: 'white', fontSize: '18px' }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </div>

      <Menu
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        inlineCollapsed={collapsed}
        style={{
          background: 'transparent',
          borderRight: 'none',
          marginTop: '8px',
        }}
        items={menuItems}
        onClick={({ key }) => navigate(key)}
        theme="dark"
      />

      <div
        onClick={logout}
        title="Sign out"
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: collapsed ? '16px 0' : '16px 24px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 12, cursor: 'pointer', color: 'rgba(255,255,255,0.65)',
        }}
      >
        <LogoutOutlined style={{ fontSize: 16 }} />
        {!collapsed && <span>Sign out</span>}
      </div>
    </Sider>
  );
};

export default Sidebar;
